import React, { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '@/contexts';
import { updateUserProfile } from '@/api/auth';
import ResponsiveOverlay from '@/components/ResponsiveOverlay';
import Image from '@/components/AppImage/AppImage';
import './AccountTypeSection.css';
import { WebAnalytics } from '@/lib/analytics';
import {
  ONBOARDING_ROLE_OPTIONS,
  type OnboardingRoleKey,
} from '@/components/Login/onboarding/OnboardingRoleSelect';
import { error as logError } from '@/utils/logger';

const ROLE_LABELS: Record<OnboardingRoleKey, string> = {
  regular: 'Traveler',
  creator: 'Creator',
  advisor: 'Travel Advisor',
};

export interface AccountTypeSectionProps {
  /** 'sm' (default) matches the mobile drawer; 'md' is larger, for the desktop popover. */
  size?: 'sm' | 'md';
  /**
   * Notifies the parent menu that the role dialog opened or closed. The nav
   * "More" popover closes on any outside click, which would unmount this
   * component — and the dialog with it — so the parent must stay open while
   * the dialog is up.
   */
  onOverlayOpenChange?: (isOpen: boolean) => void;
}

/**
 * Inline "Account Type" row (current role + "Change?") for the nav "More" menu,
 * on both the desktop popover and the mobile drawer.
 */
const AccountTypeSection: React.FC<AccountTypeSectionProps> = ({
  size = 'sm',
  onOverlayOpenChange,
}) => {
  const { user, updateUser } = useAuth();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const setSheetOpen = useCallback(
    (next: boolean) => {
      setIsSheetOpen(next);
      onOverlayOpenChange?.(next);
    },
    [onOverlayOpenChange],
  );

  const currentRole = (user?.profile_type as OnboardingRoleKey) || 'regular';

  const handleSelectRole = useCallback(
    async (role: OnboardingRoleKey) => {
      if (role === currentRole || isSaving) {
        setSheetOpen(false);
        return;
      }
      setIsSaving(true);
      try {
        await updateUserProfile({ profile_type: role });
        updateUser({ profile_type: role });
        if (user._id) {
          WebAnalytics.setUserProfile({ id: user._id, profile_type: role });
        }
        setSheetOpen(false);
      } catch (err) {
        logError('[AccountTypeSection] Error updating account type', err);
        toast.error('Failed to update account type. Please try again.');
      } finally {
        setIsSaving(false);
      }
    },
    [currentRole, isSaving, updateUser, setSheetOpen, user._id],
  );

  if (!user) return null;

  const labelSizeClass =
    size === 'md' ? 'text-[15px] leading-[20px]' : 'text-[13px] leading-[18px]';
  const valueSizeClass =
    size === 'md' ? 'text-[13px] leading-[18px]' : 'text-[11px] leading-[16px]';

  return (
    <div className="flex flex-col gap-0.5">
      <span className={`font-manrope font-bold text-gray-900 ${labelSizeClass}`}>
        Account Type
      </span>
      <div className="flex items-center gap-1">
        <span className={`font-manrope font-medium ${valueSizeClass}`} style={{ color: '#76767F' }}>
          {ROLE_LABELS[currentRole]}
        </span>
        <span className={valueSizeClass} style={{ color: '#76767F' }}>•</span>
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          className={`font-manrope font-medium text-orange-500 transition-colors hover:text-orange-600 ${valueSizeClass}`}
        >
          Change?
        </button>
      </div>

      {isSheetOpen && (
        <ResponsiveOverlay
          onClose={() => !isSaving && setSheetOpen(false)}
          presentation="dialog"
          panelClassName="account-type-dialog"
          aria-label="Account Type"
        >
          <div className="flex items-center justify-between p-5 pb-4">
            <span className="font-lora text-xl font-semibold text-gray-900">Account Type</span>
            <button
              type="button"
              onClick={() => !isSaving && setSheetOpen(false)}
              aria-label="Close"
              className="border-none bg-transparent text-xl leading-none text-gray-500 hover:text-gray-900"
            >
              ✕
            </button>
          </div>
          <div className="flex flex-col gap-2 px-5 pb-5">
            {ONBOARDING_ROLE_OPTIONS.map((role) => {
              const isSelected = currentRole === role.key;
              return (
                <button
                  key={role.key}
                  type="button"
                  disabled={isSaving}
                  onClick={() => handleSelectRole(role.key)}
                  className={`flex items-center gap-4 rounded-2xl p-3 text-left transition-colors disabled:opacity-50 ${
                    isSelected ? 'bg-orange-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <Image
                    src={role.illustration}
                    alt=""
                    width={72}
                    height={72}
                    className="account-type-role-illustration h-[72px] w-[72px] shrink-0 rounded-lg object-cover"
                  />
                  <div className="flex-1">
                    <p
                      className={`account-type-role-label text-lg font-semibold ${
                        isSelected ? 'text-orange-600' : 'text-gray-900'
                      }`}
                    >
                      {role.label}
                    </p>
                    <p
                      className={`account-type-role-headline mt-1 text-base ${
                        isSelected ? 'text-orange-400' : 'text-gray-400'
                      }`}
                    >
                      {role.headline}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </ResponsiveOverlay>
      )}
    </div>
  );
};

export default AccountTypeSection;
